/**
 * Agent 状态监控组件
 * 显示所有 Agent 的运行状态、健康度、任务信息等
 */
import { createAPIClient, type APIClient } from '../api/client.js';

export interface AgentStatus {
  groupJid: string;
  groupFolder: string | null;
  status: 'active' | 'idle' | 'error' | 'inactive';
  health: 'healthy' | 'unhealthy' | 'unknown';
  containerName: string | null;
  isTaskContainer: boolean;
  runningTaskId: string | null;
  pendingMessages: number;
  pendingTasks: number;
  currentApiKeyId?: string;
  currentApiKeyModel?: string;
  containers: Array<{
    id: string;
    status: 'ready' | 'running' | 'idle' | 'error';
    keyId?: string;
    modelName?: string;
  }>;
}

export interface AgentStatusResponse {
  agents: AgentStatus[];
  summary: {
    totalAgents: number;
    activeAgents: number;
    idleAgents: number;
    inactiveAgents: number;
    unhealthyAgents: number;
  };
}

export class AgentMonitor {
  private apiClient: APIClient;
  private container: HTMLElement | null = null;
  private refreshInterval: ReturnType<typeof setInterval> | null = null;
  private onBack: () => void;

  constructor(options: { token: string; apiBaseUrl: string; onBack: () => void }) {
    this.apiClient = createAPIClient(options.apiBaseUrl);
    this.apiClient.setToken(options.token);
    this.onBack = options.onBack;
  }

  /**
   * 初始化组件
   */
  private init(): void {
    this.container = document.getElementById('agent-monitor-content');
    if (!this.container) {
      console.error('Agent monitor container not found');
      return;
    }
  }

  /**
   * 显示组件
   */
  show(): void {
    this.init();

    const monitorScreen = document.getElementById('agent-monitor-screen');
    const chatScreen = document.getElementById('chat-screen');
    const settingsPanel = document.getElementById('settings-panel');

    if (monitorScreen) monitorScreen.style.display = 'flex';
    if (chatScreen) chatScreen.style.display = 'none';
    if (settingsPanel) settingsPanel.style.display = 'none';

    // 设置返回按钮事件
    const backBtn = document.getElementById('agent-monitor-back');
    if (backBtn) {
      backBtn.addEventListener('click', () => {
        this.hide();
        this.onBack();
      });
    }

    // 立即加载状态
    this.loadStatus();

    // 每5秒刷新一次
    this.startAutoRefresh();
  }

  /**
   * 隐藏组件
   */
  hide(): void {
    this.stopAutoRefresh();

    const monitorScreen = document.getElementById('agent-monitor-screen');
    if (monitorScreen) monitorScreen.style.display = 'none';
  }

  /**
   * 开始自动刷新
   */
  private startAutoRefresh(): void {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }

    this.refreshInterval = setInterval(() => {
      this.loadStatus();
    }, 5000);
  }

  /**
   * 停止自动刷新
   */
  private stopAutoRefresh(): void {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }
  }

  /**
   * 加载状态
   */
  private async loadStatus(): Promise<void> {
    try {
      const response = await this.apiClient.getAgentStatus();
      this.render(response);
    } catch (error) {
      console.error('Failed to load agent status:', error);
      this.showError(error instanceof Error ? error.message : '加载失败');
    }
  }

  /**
   * 渲染状态
   */
  private render(data: AgentStatusResponse): void {
    if (!this.container) return;

    const { agents, summary } = data;

    this.container.innerHTML = `
      <div class="agent-monitor">
        <!-- 概览卡片 -->
        <div class="monitor-summary">
          <h2>Agent 概览</h2>
          <div class="summary-cards">
            <div class="summary-card total">
              <div class="summary-value">${summary.totalAgents}</div>
              <div class="summary-label">总计</div>
            </div>
            <div class="summary-card active">
              <div class="summary-value">${summary.activeAgents}</div>
              <div class="summary-label">活跃</div>
            </div>
            <div class="summary-card idle">
              <div class="summary-value">${summary.idleAgents}</div>
              <div class="summary-label">空闲</div>
            </div>
            <div class="summary-card unhealthy ${summary.unhealthyAgents > 0 ? 'warning' : ''}">
              <div class="summary-value">${summary.unhealthyAgents}</div>
              <div class="summary-label">异常</div>
            </div>
          </div>
        </div>

        <!-- Agent 列表 -->
        <div class="monitor-agents">
          <h2>Agent 详情</h2>
          ${agents.length === 0 ? '<p class="no-agents">暂无 Agent</p>' : agents.map(agent => this.renderAgent(agent)).join('')}
        </div>
      </div>
    `;
  }

  /**
   * 渲染单个 Agent
   */
  private renderAgent(agent: AgentStatus): string {
    const statusClass = agent.status;
    const healthClass = agent.health === 'unhealthy' ? 'unhealthy' : '';
    const statusText = {
      'active': '运行中',
      'idle': '空闲',
      'error': '错误',
      'inactive': '未激活',
    }[agent.status];

    const healthText = {
      'healthy': '健康',
      'unhealthy': '异常',
      'unknown': '未知',
    }[agent.health];

    return `
      <div class="agent-card ${statusClass} ${healthClass}">
        <div class="agent-header">
          <div class="agent-title">
            <span class="agent-name">${agent.groupFolder || agent.groupJid}</span>
            <span class="agent-status status-${agent.status}">${statusText}</span>
            <span class="agent-health health-${agent.health}">${healthText}</span>
          </div>
          ${agent.isTaskContainer ? '<span class="task-badge">任务</span>' : ''}
        </div>

        <div class="agent-details">
          <div class="detail-row">
            <span class="detail-label">容器:</span>
            <span class="detail-value">${agent.containerName || '无'}</span>
          </div>
          ${agent.currentApiKeyModel ? `
            <div class="detail-row">
              <span class="detail-label">API Key:</span>
              <span class="detail-value">${agent.currentApiKeyModel}</span>
            </div>
          ` : ''}
          ${agent.runningTaskId ? `
            <div class="detail-row">
              <span class="detail-label">任务ID:</span>
              <span class="detail-value">${agent.runningTaskId}</span>
            </div>
          ` : ''}
          <div class="detail-row">
            <span class="detail-label">待处理消息:</span>
            <span class="detail-value">${agent.pendingMessages}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">待处理任务:</span>
            <span class="detail-value">${agent.pendingTasks}</span>
          </div>
        </div>

        ${agent.containers.length > 0 ? `
          <div class="agent-containers">
            <div class="containers-title">容器列表:</div>
            <div class="containers-list">
              ${agent.containers.map(container => `
                <div class="container-item status-${container.status}">
                  <span class="container-id">${container.id}</span>
                  <span class="container-status">${container.status}</span>
                  ${container.modelName ? `<span class="container-key">${container.modelName}</span>` : ''}
                </div>
              `).join('')}
            </div>
          </div>
        ` : ''}
      </div>
    `;
  }

  /**
   * 显示错误
   */
  private showError(message: string): void {
    if (!this.container) return;

    this.container.innerHTML = `
      <div class="monitor-error">
        <div class="error-icon">⚠️</div>
        <div class="error-message">${message}</div>
        <button class="btn btn-primary" id="retry-btn">重试</button>
      </div>
    `;

    const retryBtn = document.getElementById('retry-btn');
    if (retryBtn) {
      retryBtn.addEventListener('click', () => this.loadStatus());
    }
  }

  /**
   * 销毁组件
   */
  destroy(): void {
    this.stopAutoRefresh();
    this.container = null;
  }
}
